import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule } from '@jsverse/transloco';
import { PaymentMethodService } from '@rma/dashboard/billing/public/api-payment-methods';
import { StandardErrorMessage } from '@rma/dashboard/generic/domain/to-be-sorted';
import { StripeModule, StripeService } from '@rma/dashboard/generic/orc/stripe';
import { MatErrorOverrideComponent } from '@rma/dashboard/generic/ui/form-error';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { SetupIntent, StripeError } from '@stripe/stripe-js';
import { BehaviorSubject, catchError, filter, map, Observable, of, switchMap } from 'rxjs';

@Component({
  templateUrl: './finalise-payment-method-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    MatButtonModule,
    MatDialogModule,
    MatErrorOverrideComponent,
    MatProgressSpinnerModule,
    StripeModule,
    TranslocoModule,
  ],
})
export class FinalisePaymentMethodDialogComponent implements OnInit {
  public error$ = new BehaviorSubject<string | null>(null);
  public setupIntent$: Observable<SetupIntent | null>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: { clientSecret: string; isDefault: boolean },
    private readonly stripeService: StripeService,
    private readonly paymentMethodService: PaymentMethodService,
  ) {}

  ngOnInit(): void {
    this.setupIntent$ = this.stripeService.retrieveSetupIntent(this.data.clientSecret).pipe(
      map((x) => {
        if (x.error) {
          throw x.error;
        }

        return x.setupIntent;
      }),
      catchError((x: StripeError) => {
        this.error$.next(x.message ?? StandardErrorMessage);
        return of(null);
      }),
      rmaShareReplay(),
    );

    if (this.data.isDefault) {
      this.setupIntent$
        .pipe(
          filter((x) => !!x?.payment_method),
          map((x) => x?.payment_method),
          nonNullable(),
          // SAFETY(legacy): unreviewed, pre-dates this rule.
          switchMap((x) => this.paymentMethodService.setDefaultPaymentMethod(x as string)),
        )
        .subscribe();
    }
  }
}
